import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { Modal, Checkbox, Row, Col, Button, Select, DatePicker } from 'antd'
import { cancellationRequests, users, files } from '@services/api'
import UploadFile from './UploadFile'
import { FINANCE_USER_ID } from '@constants/index'
import { CANCELLATION_REASON_TYPES } from '@texts/cancellation'
import moment from 'moment'
import {
    BLOCKING_STATUSES,
    isPrivilegedUser,
    getReasonOptions,
    getDefaultReason,
    getReasonLabel,
    canScheduleFutureCancellation,
    getSubmittedFutureDate
} from './utils'
// הכחשת עסקה

const CANCELLATION_MANAGER_ID = 165 // keren

@inject((store: any) => {
    const { popupStore, authStore } = store
    return {
        popupStore,
        authStore
    }
})
@observer
export default class CreateCancellationRequest extends Component<any, any> {
    requestCreated = false
    // The popup stays mounted between openings; reset role defaults on each closed -> open transition.
    wasOpen = false

    constructor(props) {
        super(props)
        this.state = {
            reason: '',
            text: '',
            success: false,
            error: false,
            recipients: [FINANCE_USER_ID],
            adminUsers: [],
            showRecipients: false,
            fileData: null,
            showUpload: false,
            loading: false,
            futureCancellation: false,
            futureDate: ''
        }
    }

    componentDidMount(): void {
        this.getAdminList()
        this.resetOnOpen()
    }

    componentDidUpdate(): void {
        this.resetOnOpen()
    }

    isOpen = () => this.props.popupStore.popupType === 'CreateCancellationRequest'

    isPrivileged = () => isPrivilegedUser(this.props.authStore.roles)

    resetOnOpen = () => {
        const isOpen = this.isOpen()
        if (isOpen && !this.wasOpen) {
            this.setState({
                reason: getDefaultReason(this.isPrivileged(), this.hasValidFutureCancellation()),
                futureCancellation: false,
                futureDate: ''
            })
        }
        this.wasOpen = isOpen
    }

    canScheduleFuture = () =>
        canScheduleFutureCancellation(
            this.isPrivileged(),
            !!(this.props.popupStore.info && this.props.popupStore.info.specialSub),
            this.hasValidFutureCancellation()
        )

    getClientInfo = () => (this.props.popupStore.info && this.props.popupStore.info.clientInfo) || {}

    getOpenCancellationStatus = () => {
        const {
            popupStore: { info }
        } = this.props
        const clientInfo = info && info.clientInfo
        return clientInfo && clientInfo.open_cancellation_status ? clientInfo.open_cancellation_status : null
    }

    // All three markers must agree for a scheduled cancellation to be genuine. Only these
    // may be superseded by a new request, and only the backend's three whitelisted reasons.
    hasValidFutureCancellation = () => {
        const clientInfo = this.getClientInfo()
        return (
            clientInfo.open_cancellation_status === 'FUTURE_CANCELLATION' &&
            clientInfo.open_cancellation_type === 'FUTURE' &&
            !!clientInfo.future_cancellation_date
        )
    }

    // Status says scheduled but the type or the date is missing. Known bad data. The
    // backend refuses to supersede it, so the form stays hidden — but the operator gets an
    // explanation and a link instead of a generic "request already open".
    hasMalformedFutureCancellation = () => {
        const status = this.getOpenCancellationStatus()
        return status === 'FUTURE_CANCELLATION' && !this.hasValidFutureCancellation()
    }

    hasBlockingRequest = () => {
        const status = this.getOpenCancellationStatus()
        return BLOCKING_STATUSES.indexOf(status) !== -1
    }

    getFutureWarningText = () => {
        const clientInfo = this.getClientInfo()
        const reason = CANCELLATION_REASON_TYPES[clientInfo.open_cancellation_reason] || ''
        const parsed = clientInfo.future_cancellation_date
            ? moment(clientInfo.future_cancellation_date, 'YYYY-MM-DD')
            : null
        if (parsed && parsed.isValid()) {
            return `שים לב: קיימת בקשת ביטול עתידי לתאריך ${parsed.format('DD/MM/YYYY')} מסיבת ${reason}`
        }
        return `שים לב: קיימת בקשת ביטול עתידי ללא תאריך מסיבת ${reason}`
    }

    renderRequestLink = () => {
        const clientInfo = this.getClientInfo()
        if (!clientInfo.open_cancellation_id) return null
        return (
            <a
                href={`/cancelSubRequests/${clientInfo.open_cancellation_id}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {'לצפייה או עריכת הבקשה'}
            </a>
        )
    }

    renderFutureWarning = () => {
        return (
            <div
                style={{
                    border: 'solid #fa8c16 1px',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    marginBottom: '15px',
                    color: '#fa8c16'
                }}
            >
                <strong>{this.getFutureWarningText()}</strong>
                <div style={{ marginTop: '8px' }}>{this.renderRequestLink()}</div>
            </div>
        )
    }

    getAdminList = () => {
        users
            .getUsersList()
            .then(res => this.setState({ adminUsers: res.data }))
            .catch(error => console.log(error))
    }

    closePopup = () => {
        const {
            popupStore: { togglePopup }
        } = this.props
        this.setState({
            reason: '',
            text: '',
            success: false,
            error: false,
            showRecipients: false,
            recipients: [FINANCE_USER_ID],
            fileData: null,
            showUpload: false,
            loading: false,
            futureCancellation: false,
            futureDate: ''
        })
        togglePopup(false)

        if (this.requestCreated) {
            this.requestCreated = false
            if (typeof this.props.updateClientInfo === 'function') {
                this.props.updateClientInfo()
            }
        }
    }

    // labelInValue: the restricted default (CUSTOMER_REQUEST) is not an option, so its label must come
    // from the value. Clearing returns to the role default.
    handleReasonSelect = value => {
        this.setState({
            reason: value ? value.key : getDefaultReason(this.isPrivileged(), this.hasValidFutureCancellation())
        })
    }

    handleTextInput = e => {
        this.setState({ text: e.target.value })
    }

    handleCheckRecipients = recipients => {
        const nextRecipients = recipients.find(id => `${id}` === `${FINANCE_USER_ID}`)
            ? recipients
            : [...recipients, FINANCE_USER_ID]
        this.setState({ recipients: nextRecipients })
    }

    sendRequest = () => {
        const {
            popupStore: {
                info: { clientInfo, specialSub, getSpecialSubscriptions }
            }
        } = this.props

        const {
            authStore: { id }
        } = this.props

        const { reason, text, recipients, fileData } = this.state
        const requestRecipients = recipients && recipients.length ? recipients : [FINANCE_USER_ID]

        const payload = {
            createdBy: id,
            requestedFor: clientInfo.id,
            requestReason: reason,
            message: text,
            recipients: requestRecipients,
            invitation_id: '',
            userSpecialSubId: specialSub ? specialSub.id : null,
            future_cancellation_date: getSubmittedFutureDate(
                this.canScheduleFuture(),
                this.state.futureCancellation,
                this.state.futureDate
            ),
            // Explicit origin: the API keeps the automatic agent email for admin-panel requests.
            source: 'admin-panel'
        }

        this.setState({ success: false, error: false, loading: true }, () => {
            cancellationRequests
                .postCancellationRequest(payload)
                .then(res => {
                    this.requestCreated = true
                    if (fileData) {
                        return files.upload(fileData.formData, { ...fileData.details, related: res.data.requestId })
                    } else {
                        this.setState({ success: true })
                    }
                })
                .then(res => {
                    if (res) {
                        const file = JSON.stringify(res.data)
                        const requestId = res.data.related_id
                        return cancellationRequests.updateCancellationRequest({ requestId, file })
                    } else {
                        this.setState({ success: true })
                    }
                })
                .then(() => {
                    this.setState({ success: true })
                })
                .catch(err => {
                    if (err && err.response && err.response.data && err.response.data.key) {
                        this.setState({ error: this.errorMsgs[err.response.data.key] || 'אירעה שגיאה' })
                    } else if (
                        err &&
                        err.request &&
                        err.request.responseURL &&
                        err.request.responseURL.includes('/files')
                    ) {
                        this.setState({ error: 'אירעה שגיאה בעת העלאת הקובץ' })
                    } else {
                        this.setState({ error: 'אירעה שגיאה' })
                    }
                })
                .then(() => {
                    if (specialSub) {
                        typeof getSpecialSubscriptions === 'function' && getSpecialSubscriptions()
                    }
                    this.setState({ loading: false })
                })
        })
    }

    errorMsgs = {
        OPEN_REQUEST: 'קיימת בקשת ביטול פתוחה עבור לקוח זה',
        UPDATE_FAILED: 'אירעה שגיאה בעת העלאת הקובץ'
    }

    render() {
        const {
            popupStore: { info, popupType }
        } = this.props
        const { specialSub } = info

        const { reason, text, success, error, adminUsers, showRecipients, showUpload, loading } = this.state
        const privileged = this.isPrivileged()

        const alwaysDisabled = { [FINANCE_USER_ID]: FINANCE_USER_ID }

        let showManager = false

        if (['CUSTOMER_REQUEST', 'OTHER'].includes(reason)) {
            showManager = true
        } else {
            showManager = false
        }

        let content

        if (info.clientInfo) {
            if (this.hasMalformedFutureCancellation() && !success) {
                content = this.renderFutureWarning()
            } else if (this.hasBlockingRequest() && !success) {
                content = (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <h4>{this.errorMsgs.OPEN_REQUEST}</h4>
                        {this.renderRequestLink()}
                    </div>
                )
            } else if (!success && !error) {
                const { first_name, last_name, passport, identity, id } = info.clientInfo
                content = (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-evenly',
                            minHeight: '100px'
                        }}
                        className="create-cancellation-req-content"
                    >
                        {this.hasValidFutureCancellation() && this.renderFutureWarning()}
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '15px'
                            }}
                        >
                            <span>{`הלקוח: ${first_name} ${last_name}`}</span>
                            {identity && <span>{`ת.ז: ${identity}`}</span>}
                            {passport && <span>{`מ.דרכון: ${passport}`}</span>}
                            <span>{`מזהה לקוח: ${id}`}</span>
                        </div>

                        {specialSub ? <span>{`המינוי: ${specialSub.subscription_name}`}</span> : ''}

                        <Select
                            style={{ width: '100%', marginTop: '1rem', color: 'black' }}
                            labelInValue
                            value={reason ? { key: reason, label: getReasonLabel(reason) } : undefined}
                            allowClear={!privileged && !this.hasValidFutureCancellation()}
                            onChange={e => this.handleReasonSelect(e)}
                            placeholder="סיבת הביטול"
                        >
                            {getReasonOptions(privileged, this.hasValidFutureCancellation()).map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>

                        {reason && (
                            <div
                                style={{
                                    border: 'solid #d9d9d9 1px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: '15px',
                                    borderRadius: '4px'
                                }}
                            >
                                <span>פירוט:</span>
                                <textarea
                                    style={{ border: 'none', outline: 'none' }}
                                    onChange={this.handleTextInput}
                                    name=""
                                    id=""
                                    cols={30}
                                    rows={10}
                                ></textarea>
                            </div>
                        )}
                        {this.canScheduleFuture() && (
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                <Checkbox
                                    checked={this.state.futureCancellation}
                                    onChange={e =>
                                        this.setState({
                                            futureCancellation: e.target.checked,
                                            futureDate: e.target.checked ? this.state.futureDate : ''
                                        })
                                    }
                                >
                                    {'ביטול עתידי'}
                                </Checkbox>
                                {this.state.futureCancellation && (
                                    <DatePicker
                                        format={'DD/MM/YYYY'}
                                        placeholder={'תאריך ביטול עתיד'}
                                        value={this.state.futureDate ? moment(this.state.futureDate) : null}
                                        onChange={e =>
                                            this.setState({
                                                futureDate: e ? e.format('YYYY-MM-DD 00:00:00') : ''
                                            })
                                        }
                                        disabledDate={current => !!(current && current < moment().endOf('day'))}
                                        allowClear={false}
                                        style={{ marginRight: '10px' }}
                                    />
                                )}
                            </div>
                        )}
                        <div style={{ position: 'relative', height: '20px' }}>
                            {!showUpload && (
                                <Button onClick={() => this.setState({ showUpload: true })}>{'הוספת קובץ'}</Button>
                            )}

                            {showUpload && (
                                <Button onClick={() => this.setState({ showUpload: false, fileData: null })}>
                                    {'ביטול הוספת קובץ'}
                                </Button>
                            )}

                            {reason && text && (!this.state.futureCancellation || this.state.futureDate) && (
                                <Button
                                    disabled={loading}
                                    style={{ position: 'absolute', right: 'calc(50% - 46px)' }}
                                    onClick={this.sendRequest}
                                >
                                    שלח בקשה
                                </Button>
                            )}
                            {!showRecipients ? (
                                <Button
                                    onClick={() => this.setState({ showRecipients: true })}
                                    style={{ position: 'absolute', left: '0', marginLeft: '0' }}
                                >
                                    מכותבים
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => this.setState({ showRecipients: false })}
                                    style={{ position: 'absolute', left: '0', marginLeft: '0' }}
                                >
                                    סגור רשימה
                                </Button>
                            )}
                        </div>
                        <div style={{ ...recipientStyle, display: `${showRecipients === true ? 'flex' : 'none'}` }}>
                            <Checkbox.Group
                                value={this.state.recipients}
                                onChange={recipients => this.handleCheckRecipients(recipients)}
                            >
                                {adminUsers.map((user, index) => {
                                    if (!showManager && user.id === CANCELLATION_MANAGER_ID) {
                                        return null
                                    }
                                    return (
                                        <Row style={{ marginBottom: '20px' }} key={index.toString()}>
                                            <Col>
                                                <Checkbox
                                                    disabled={alwaysDisabled[user.id] ? true : false}
                                                    value={user.id}
                                                >
                                                    <span style={{ marginBottom: '20px' }}>
                                                        <span
                                                            style={{ fontSize: '16px' }}
                                                        >{`${user.first_name} ${user.last_name}`}</span>
                                                        <span
                                                            style={{ marginRight: '20px', fontSize: '16px' }}
                                                        >{`${user.email}`}</span>
                                                    </span>
                                                </Checkbox>
                                            </Col>
                                        </Row>
                                    )
                                })}
                            </Checkbox.Group>
                        </div>

                        {showUpload && (
                            <div style={{ marginTop: '25px' }}>
                                <UploadFile setFileData={fileData => this.setState({ fileData })} />
                            </div>
                        )}
                    </div>
                )
            } else if (error) {
                content = (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <h4>{this.state.error}</h4>
                    </div>
                )
            } else if (success) {
                content = (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <h4>{'הבקשה נשלחה בהצלחה'}</h4>
                    </div>
                )
            }
        } else {
            content = (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <h4>{'לא ניתן להציג את הטופס'}</h4>
                </div>
            )
        }

        return (
            <Modal
                className="create-cancellation-req-popup"
                title={info.title}
                visible={popupType === 'CreateCancellationRequest'}
                onCancel={this.closePopup}
                footer={null}
            >
                {content}
            </Modal>
        )
    }
}

const recipientStyle = {
    padding: '20px',
    border: 'solid #d9d9d9 1px',
    borderRadius: '4px',
    maxHeight: '300px',
    overflow: 'scroll',
    marginTop: '20px'
}
